import moment from 'moment'
import { CvsAutoComplete, CvsDatePicker } from '@components'
import { ALL_USERS } from '../../config'
import { useApprovalContext } from '../../contexts'
import { ActionType } from '../../contexts/approvalContext/types'
import { createSubmittedByOptions, fuzzySearch, getRequestsWithinDateRange } from '../../utils'
import { ResetFiltersButton } from '../ResetFiltersButton'
import * as css from './styles'
import { t } from '@lingui/core/macro'

const DATE_FORMAT = 'YYYY-MM-DD'

export const Filters = () => {
  const {
    approvalRequests,
    filtersHaveChanged,
    isLoading,
    requestsBySubmitter,
    requestsWithinDateRange,
    submittedBy,
    submittedByOptions,
    submittedFrom,
    submittedTo,
    dispatch,
    showSpinner,
  } = useApprovalContext() ?? {
    approvalRequests: [],
    filtersHaveChanged: false,
    isLoading: false,
    requestsBySubmitter: [],
    requestsWithinDateRange: [],
    submittedBy: { value: 0, label: '' },
    submittedByOptions: [],
    submittedFrom: null,
    submittedTo: null,
    dispatch: () => void {},
    showSpinner: () => void {},
  }
  const disableAll = Boolean(!approvalRequests?.length)
  const disableSubmittedBy = disableAll || isLoading || submittedByOptions.length === 1

  return (
    <>
      <css.FilterContainer css={css.autocompleteContainer}>
        <CvsAutoComplete
          label={t`Submitted by`}
          options={submittedByOptions as never[]}
          margin="dense"
          disabled={disableSubmittedBy}
          style={css.selectStyle}
          skin="white"
          isLoading={isLoading}
          selected={submittedBy.value.toString()}
          onSelected={handleChangeSubmittedBy}
          allowMissingValueErrors={false}
        />
      </css.FilterContainer>
      <css.FilterContainer css={css.datepickerContainer}>
        <CvsDatePicker
          disabled={disableAll || isLoading}
          color="primary"
          format={DATE_FORMAT}
          label={t`Submitted from`}
          handleChange={handleChangeSubmittedFrom}
          value={submittedFrom ? submittedFrom.toDate() : undefined}
          margin="dense"
          shouldDisableDate={disableDatesAfter}
          shrinkLabel
        />
      </css.FilterContainer>
      <css.FilterContainer css={css.datepickerContainer}>
        <CvsDatePicker
          disabled={disableAll || isLoading}
          color="primary"
          format={DATE_FORMAT}
          label={t`Submitted to`}
          handleChange={handleChangeSubmittedTo}
          value={submittedTo?.toDate()}
          margin="dense"
          shouldDisableDate={disableDatesBefore}
        />
      </css.FilterContainer>
      {filtersHaveChanged && (
        <css.FilterContainer>
          <ResetFiltersButton />
        </css.FilterContainer>
      )}
    </>
  )

  function handleChangeSubmittedBy(optionValue: number) {
    const selectedOption = submittedByOptions.find(option => option.value === optionValue)

    if (selectedOption?.label === ALL_USERS.label) {
      removeSubmittedByFilter()
      return
    }

    showSpinner(() => {
      const searchResults = fuzzySearch(selectedOption?.label ?? '', requestsWithinDateRange)
      dispatch({
        type: ActionType.handleUpdateSubmittedBy,
        payload: {
          submittedBy: selectedOption,
          requestsBySubmitter: searchResults,
        },
      })
    })
  }

  function removeSubmittedByFilter() {
    showSpinner(() => {
      dispatch({
        type: ActionType.handleUpdateSubmittedBy,
        payload: {
          submittedBy: ALL_USERS,
          requestsBySubmitter: null,
        },
      })
    })
  }

  function handleChangeSubmittedFrom(from: moment.Moment) {
    const startOfDay = from.startOf('day')

    if (startOfDay.isSame(submittedFrom)) return

    const withinDateRange = getRequestsWithinDateRange({
      requests: requestsBySubmitter ?? approvalRequests ?? [],
      start: startOfDay,
      end: submittedTo,
    })

    const submitters = createSubmittedByOptions(withinDateRange)

    showSpinner(() => {
      dispatch({
        type: ActionType.handleUpdateSubmittedFrom,
        payload: {
          submittedFrom: startOfDay,
          requestsWithinDateRange: withinDateRange,
          submittedByOptions: [ALL_USERS, ...submitters],
        },
      })
    })
  }

  function handleChangeSubmittedTo(to: moment.Moment) {
    const endOfDay = to.endOf('day')

    if (endOfDay.isSame(submittedFrom)) return

    const withinDateRange = getRequestsWithinDateRange({
      requests: requestsBySubmitter ?? approvalRequests ?? [],
      start: submittedFrom,
      end: endOfDay,
    })

    const submitters = createSubmittedByOptions(withinDateRange)

    showSpinner(() => {
      dispatch({
        type: ActionType.handleUpdateSubmittedTo,
        payload: {
          submittedTo: endOfDay,
          requestsWithinDateRange: withinDateRange,
          submittedByOptions: [ALL_USERS, ...submitters],
        },
      })
    })
  }

  function disableDatesBefore(date: moment.Moment) {
    return date.isBefore(submittedFrom)
  }

  function disableDatesAfter(date: moment.Moment) {
    return date.isAfter(submittedTo)
  }
}
